import config from "@~/envConfig";
import { OnlineStockData, Product, ProductListConfig } from "@~/types/types";
import { getVariant } from "@~/contexts/VariantContext/utils";
import { reportClientError } from "@~/utils/reportClientError";
import { clientLogger } from "@~/utils/clientLogger";

const API_URL = config.onlineStockBaseUrl;
const API_KEY = config.onlineStockApiKey;
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

export async function fetchOnlineStockDataForProducts(
  products: Product[],
  configName: string | undefined,
  consumerId: string,
  instanceId?: string,
): Promise<OnlineStockData[]> {
  const skuIds = products
    .map((product) => {
      const variant = getVariant({
        product,
        userSelectedColour: null,
      });
      return variant?.aliases.skuId;
    })
    .filter((skuID) => skuID !== undefined);
  return fetchOnlineStockData(skuIds, configName, consumerId, instanceId);
}

export async function fetchOnlineStockData(
  skuIds: string[],
  configName: string | undefined,
  consumerId: string,
  instanceId?: string,
): Promise<OnlineStockData[]> {
  const url = `${API_URL}?key=${API_KEY}&skuIds=${skuIds.join(",")}`;

  const newrelicAttributes = {
    api: "onlineStock",
    skuIds: skuIds.join(","),
    config: configName,
    consumerId,
    instanceId,
  };

  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(url, { credentials: "include" });

      if (!res.ok) {
        const error = new Error("Error calling Online Stock service");
        if (attempt < MAX_RETRIES) {
          clientLogger.warn(
            `Online Stock API returned ${res.status}. Retrying... (attempt ${attempt + 1}/${MAX_RETRIES})`,
          );
          await delay(RETRY_DELAY_MS * (attempt + 1));
          continue;
        }
        reportClientError(error, {
          ...newrelicAttributes,
          status: res.status,
        });
        throw error;
      }

      return (
        (await res
          .json()
          .then(({ stocks }) => validateOnlineStockData(stocks))) ?? []
      );
    } catch (error) {
      if (attempt < MAX_RETRIES) {
        clientLogger.warn(
          `Online Stock API fetch failed: ${error instanceof Error ? error.message : "Unknown error"}. Retrying... (attempt ${attempt + 1}/${MAX_RETRIES})`,
        );
        lastError = error instanceof Error ? error : new Error(String(error));
        await delay(RETRY_DELAY_MS * (attempt + 1));
      } else {
        reportClientError(error, newrelicAttributes);
        throw error;
      }
    }
  }

  // This should never be reached, but TypeScript requires a return statement
  throw lastError || new Error("Online Stock API fetch failed");
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const validateOnlineStockData = (data: unknown): OnlineStockData[] | null => {
  if (!Array.isArray(data)) return null;

  return data.every((item) => {
    if (typeof item !== "object" || item === null) return false;
    const { availabilityStatus, stockMessage, stockQuantity, skuId } = item;
    if (typeof availabilityStatus !== "string") return false;
    if (typeof stockMessage !== "string") return false;
    if (typeof stockQuantity !== "number") return false;
    if (typeof skuId !== "string") return false;
    return true;
  })
    ? data
    : null;
};

export const shouldFetchOnlineStock = (config: ProductListConfig) => {
  return isComponentInConfig("StockMessage", config);
};

const isComponentInConfig = (
  componentName: string,
  config: ProductListConfig,
) => {
  const listOfComponentsInConfig = Object.values(
    config["product-card-slots"] ?? {},
  )
    .flat()
    .map(({ name }) => name);

  return listOfComponentsInConfig.includes(componentName);
};
